/* ==========================================================================
   Leg Lush — Product detail page behaviour
   Vanilla, no dependencies, loaded with `defer`.

   Everything here is progressive enhancement on top of Dawn:
   cart + variant logic stays inside Dawn's <product-form>, <variant-selects>
   and <product-info> custom elements. This file never touches them.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return REDUCE.matches;
  }

  function raf(fn) {
    return window.requestAnimationFrame(fn);
  }

  /* ------------------------------------------------------------------------
     Focus trap — small and self-contained so the preview page behaves
     identically to the storefront.
     ---------------------------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function focusableWithin(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  /* ------------------------------------------------------------------------
     <ll-modal> — dialog shell used by the size guide and the lightbox.
     Implements Dawn's ModalDialog API (show/hide) so Dawn's <modal-opener>
     can drive it unchanged.
     ---------------------------------------------------------------------- */

  var scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount += 1;
    document.documentElement.style.setProperty('overflow', 'hidden');
    document.body.style.setProperty('overflow', 'hidden');
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }
  }

  if (!window.customElements.get('ll-modal')) {
    window.customElements.define(
      'll-modal',
      class LlModal extends HTMLElement {
        connectedCallback() {
          if (this.__bound) return;
          this.__bound = true;

          this.dialog = this.querySelector('[role="dialog"]');
          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('keydown', this.onKeydown.bind(this));
        }

        onClick(event) {
          if (event.target.closest('[data-ll-modal-close]') || event.target.hasAttribute('data-ll-modal-scrim')) {
            this.hide();
          }
        }

        onKeydown(event) {
          if (event.key === 'Escape') {
            event.stopPropagation();
            this.hide();
            return;
          }
          if (event.key !== 'Tab') return;

          var items = focusableWithin(this.dialog || this);
          if (!items.length) return;
          var first = items[0];
          var last = items[items.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        /** @param {HTMLElement} [opener] element that gets focus back on close */
        show(opener) {
          if (this.hasAttribute('open')) return;
          this.openedBy = opener || document.activeElement;
          this.removeAttribute('hidden');
          this.setAttribute('open', '');
          lockScroll();
          this.dispatchEvent(new CustomEvent('ll-modal:open', { bubbles: true }));

          var target = this.querySelector('[data-ll-autofocus]') || this.dialog || this;
          raf(function () {
            target.focus();
          });
        }

        hide() {
          if (!this.hasAttribute('open')) return;
          this.removeAttribute('open');
          this.setAttribute('hidden', '');
          unlockScroll();
          this.dispatchEvent(new CustomEvent('ll-modal:close', { bubbles: true }));
          if (this.openedBy && typeof this.openedBy.focus === 'function') this.openedBy.focus();
        }
      }
    );
  }

  /* Dawn defines <modal-opener>; define a matching fallback for the static
     preview page (and for any page that does not load global.js). */
  if (!window.customElements.get('modal-opener')) {
    window.customElements.define(
      'modal-opener',
      class LlModalOpener extends HTMLElement {
        connectedCallback() {
          if (this.__bound) return;
          this.__bound = true;
          var button = this.querySelector('button');
          if (!button) return;
          var selector = this.getAttribute('data-modal');
          button.addEventListener('click', function () {
            var modal = document.querySelector(selector);
            if (modal && typeof modal.show === 'function') modal.show(button);
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <ll-gallery>
     One DOM, two layouts: a scroll-snap carousel with dots under 990px,
     a stacked/mosaic scroll column above it. Clicking a frame opens the
     lightbox at that index.
     ---------------------------------------------------------------------- */

  if (!window.customElements.get('ll-gallery')) {
    window.customElements.define(
      'll-gallery',
      class LlGallery extends HTMLElement {
        connectedCallback() {
          if (this.__bound) return;
          this.__bound = true;

          this.list = this.querySelector('.ll-gallery__list');
          this.items = Array.prototype.slice.call(this.querySelectorAll('.ll-gallery__item'));
          this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-ll-dot]'));
          this.counter = this.querySelector('[data-ll-counter-current]');
          this.index = 0;

          if (!this.list || !this.items.length) return;

          this.onScroll = this.onScroll.bind(this);
          this.list.addEventListener('scroll', this.onScroll, { passive: true });

          var self = this;
          this.dots.forEach(function (dot) {
            dot.addEventListener('click', function () {
              self.goTo(parseInt(dot.getAttribute('data-ll-dot'), 10));
            });
          });

          this.addEventListener('click', function (event) {
            var trigger = event.target.closest('[data-ll-zoom]');
            if (!trigger) return;
            var lightbox = document.querySelector(self.getAttribute('data-lightbox') || '');
            if (!lightbox || typeof lightbox.openAt !== 'function') return;
            event.preventDefault();
            lightbox.openAt(parseInt(trigger.getAttribute('data-ll-zoom'), 10), trigger);
          });

          this.observeDesktop();
        }

        /** Desktop column: report whichever media fills most of the viewport. */
        observeDesktop() {
          if (!('IntersectionObserver' in window) || !this.counter) return;
          var self = this;
          this.io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.intersectionRatio < 0.55) return;
                var i = self.items.indexOf(entry.target);
                if (i > -1) self.setIndex(i);
              });
            },
            { threshold: [0.55, 0.85] }
          );
          this.items.forEach(function (item) {
            self.io.observe(item);
          });
        }

        /* The carousel is inset by --ll-gutter so it shares the page's left
           edge on a phone, which means a frame is narrower than the distance
           between two frames. Both of these used to assume they were equal. */
        step() {
          if (this.items.length < 2) return this.items[0].getBoundingClientRect().width || 1;
          return this.items[1].offsetLeft - this.items[0].offsetLeft || 1;
        }

        onScroll() {
          if (this.scrollTick) return;
          var self = this;
          this.scrollTick = raf(function () {
            self.scrollTick = null;
            self.setIndex(Math.round(self.list.scrollLeft / self.step()));
          });
        }

        setIndex(i) {
          i = Math.max(0, Math.min(i, this.items.length - 1));
          if (i === this.index && this.__painted) return;
          this.index = i;
          this.__painted = true;

          this.dots.forEach(function (dot, di) {
            var on = di === i;
            dot.setAttribute('aria-current', on ? 'true' : 'false');
          });
          if (this.counter) this.counter.textContent = String(i + 1);
        }

        goTo(i) {
          var item = this.items[i];
          if (!item) return;
          this.list.scrollTo({
            left: Math.max(0, i) * this.step(),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
          this.setIndex(i);
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <ll-lightbox> — full-screen media viewer with click-to-zoom + pan.
     Extends the modal shell above by composition, not inheritance, so the
     element stays registered as <ll-modal> for the opener API.
     ---------------------------------------------------------------------- */

  function initLightbox(modal) {
    if (modal.__lbBound) return;
    modal.__lbBound = true;

    var track = modal.querySelector('[data-ll-lb-track]');
    var slides = Array.prototype.slice.call(modal.querySelectorAll('.ll-lightbox__slide'));
    var thumbs = Array.prototype.slice.call(modal.querySelectorAll('[data-ll-lb-thumb]'));
    var current = modal.querySelector('[data-ll-lb-current]');
    var prev = modal.querySelector('[data-ll-lb-prev]');
    var next = modal.querySelector('[data-ll-lb-next]');
    if (!track || !slides.length) return;

    var index = 0;

    function paint(i) {
      index = Math.max(0, Math.min(i, slides.length - 1));
      if (current) current.textContent = String(index + 1);
      thumbs.forEach(function (thumb, ti) {
        thumb.setAttribute('aria-current', ti === index ? 'true' : 'false');
      });
      slides.forEach(function (slide, si) {
        slide.setAttribute('aria-hidden', si === index ? 'false' : 'true');
        if (si !== index) unzoom(slide);
      });
    }

    function scrollTo(i, instant) {
      var slide = slides[Math.max(0, Math.min(i, slides.length - 1))];
      if (!slide) return;
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth',
      });
      paint(i);
    }

    function unzoom(slide) {
      slide.classList.remove('is-zoomed');
      var img = slide.querySelector('img');
      if (img) img.style.removeProperty('transform-origin');
      track.classList.remove('is-zoom-locked');
    }

    track.addEventListener(
      'scroll',
      function () {
        if (track.__tick) return;
        track.__tick = raf(function () {
          track.__tick = null;
          var width = slides[0].getBoundingClientRect().width || 1;
          paint(Math.round(track.scrollLeft / width));
        });
      },
      { passive: true }
    );

    if (prev) {
      prev.addEventListener('click', function () {
        scrollTo(index - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        scrollTo(index + 1);
      });
    }

    thumbs.forEach(function (thumb, ti) {
      thumb.addEventListener('click', function () {
        scrollTo(ti);
      });
    });

    // Click to zoom, move to pan.
    slides.forEach(function (slide) {
      var img = slide.querySelector('img');
      if (!img) return;

      img.addEventListener('click', function (event) {
        if (slide.classList.contains('is-zoomed')) {
          unzoom(slide);
          return;
        }
        var rect = img.getBoundingClientRect();
        var x = ((event.clientX - rect.left) / rect.width) * 100;
        var y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
        slide.classList.add('is-zoomed');
        track.classList.add('is-zoom-locked');
      });

      img.addEventListener('mousemove', function (event) {
        if (!slide.classList.contains('is-zoomed')) return;
        var rect = img.getBoundingClientRect();
        var x = ((event.clientX - rect.left) / rect.width) * 100;
        var y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
    });

    modal.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollTo(index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollTo(index - 1);
      }
    });

    modal.addEventListener('ll-modal:close', function () {
      slides.forEach(unzoom);
    });

    modal.openAt = function (i, opener) {
      /* The slides are lazy and live inside a hidden dialog, so they never
         intersect anything and never fetch: an unloaded <img> with height:auto
         and no reserved box computes to 0x0, which is why the overlay opened
         onto nothing. Opening is the moment they are needed, so ask for them
         eagerly the first time. */
      if (!modal.__imagesPrimed) {
        modal.__imagesPrimed = true;
        var imgs = modal.querySelectorAll('img[loading="lazy"]');
        for (var n = 0; n < imgs.length; n++) {
          imgs[n].setAttribute('loading', 'eager');
          if (imgs[n].dataset && imgs[n].dataset.src && !imgs[n].getAttribute('src')) {
            imgs[n].setAttribute('src', imgs[n].dataset.src);
          }
        }
      }
      modal.show(opener);
      paint(i);
      raf(function () {
        raf(function () {
          scrollTo(i, true);
        });
      });
    };

    paint(0);
  }

  /* ------------------------------------------------------------------------
     Size guide — cm / inch toggle. Values live in the markup as data
     attributes so the table is correct before JS runs.
     ---------------------------------------------------------------------- */

  function initSizeGuide(root) {
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-ll-unit]'));
    var cells = Array.prototype.slice.call(root.querySelectorAll('[data-cm]'));
    if (!buttons.length || !cells.length) return;

    function apply(unit) {
      cells.forEach(function (cell) {
        cell.textContent = unit === 'in' ? cell.getAttribute('data-in') : cell.getAttribute('data-cm');
      });
      buttons.forEach(function (button) {
        button.setAttribute('aria-pressed', button.getAttribute('data-ll-unit') === unit ? 'true' : 'false');
      });
      root.querySelectorAll('[data-ll-unit-label]').forEach(function (label) {
        label.textContent = unit === 'in' ? 'inches' : 'cm';
      });
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        apply(button.getAttribute('data-ll-unit'));
      });
    });

    apply('cm');
  }

  /* ------------------------------------------------------------------------
     Sticky mobile add-to-bag bar
     ---------------------------------------------------------------------- */

  function initStickyBar(bar) {
    var atcId = bar.getAttribute('data-atc');
    var atc = atcId ? document.getElementById(atcId) : null;
    var mirror = bar.querySelector('[data-ll-sticky-button]');
    if (!atc) return;

    var root = bar.closest('product-info') || document;

    /* Keep the bar honest about what is actually in the form: the selected
       size and the live price, formatted by the same Liquid filter as the buy
       box rather than a second one. */
    var sizeOut = bar.querySelector('[data-ll-sticky-variant]');
    var priceOut = bar.querySelector('[data-ll-sticky-price]');

    if (sizeOut) {
      var picker = root.querySelector('variant-selects');
      if (picker) {
        picker.addEventListener('change', function (event) {
          if (event.target && event.target.type === 'radio') sizeOut.textContent = event.target.value;
        });
      }
    }

    if (priceOut) {
      var priceNode = root.querySelector('[id^="price-"]');
      if (priceNode) {
        var copyPrice = function () {
          var now = priceNode.querySelector('.ll-price__now');
          if (now) priceOut.textContent = now.textContent.trim();
        };
        copyPrice();
        new MutationObserver(copyPrice).observe(priceNode, { childList: true, subtree: true, characterData: true });
      }
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', scrolledPast);
        },
        { threshold: 0 }
      ).observe(atc);
    }

    if (!mirror) return;

    // Keep the mirrored button in step with Dawn's real submit button.
    var sync = function () {
      var disabled = atc.hasAttribute('disabled');
      mirror.toggleAttribute('disabled', disabled);
      var label = atc.querySelector('span');
      var mirrorLabel = mirror.querySelector('span');
      if (label && mirrorLabel) mirrorLabel.textContent = label.textContent.trim();
    };
    sync();
    new MutationObserver(sync).observe(atc, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });
  }

  /* ------------------------------------------------------------------------
     Selected-size label mirror.
     Dawn's VariantSelects does this too; running it here as well keeps the
     static preview honest and is idempotent (same string, same node).
     ---------------------------------------------------------------------- */

  function initPickerLabels(scope) {
    scope.querySelectorAll('.ll-picker').forEach(function (fieldset) {
      fieldset.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.type !== 'radio') return;
        var out = fieldset.querySelector('[data-selected-value]');
        if (out) out.textContent = input.value;
      });
    });
  }

  /* ------------------------------------------------------------------------
     Stat count-up
     ---------------------------------------------------------------------- */

  /* True once the visitor has actually done something. Stats that come into
     view before any interaction (or on a page a screenshot tool just loaded)
     are painted at their final value instead of animating from zero — a
     half-counted number is worse than no animation. */
  var interacted = false;
  ['scroll', 'pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'].forEach(function (type) {
    window.addEventListener(
      type,
      function () {
        interacted = true;
      },
      { once: true, passive: true }
    );
  });

  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-ll-count'));
    if (isNaN(target)) return;
    var decimals = (el.getAttribute('data-ll-decimals') || '0') | 0;
    var suffix = el.getAttribute('data-ll-suffix') || '';

    if (prefersReducedMotion() || !interacted) {
      el.textContent = target.toFixed(decimals) + suffix;
      return;
    }

    var duration = 1400;
    var start = null;

    function step(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (target * eased).toFixed(decimals) + suffix;
      if (p < 1) raf(step);
    }
    raf(step);
  }

  function initStats(scope) {
    var values = Array.prototype.slice.call(scope.querySelectorAll('[data-ll-count]'));
    if (!values.length) return;

    if (!('IntersectionObserver' in window)) {
      values.forEach(countUp);
      return;
    }

    var io = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          countUp(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.4 }
    );
    values.forEach(function (value) {
      io.observe(value);
    });
  }

  /* ------------------------------------------------------------------------
     Testimonial marquee — duplicate the track once so translateX(-50%) loops
     seamlessly. Skipped under reduced motion (CSS turns it into a scroller).
     ---------------------------------------------------------------------- */

  function startMarquee(list) {
    if (list.__doubled || prefersReducedMotion()) return;
    list.__doubled = true;
    Array.prototype.slice.call(list.children).forEach(function (child) {
      var clone = child.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      list.appendChild(clone);
    });
    list.classList.add('is-running');
  }

  function initMarquee(scope) {
    scope.querySelectorAll('[data-ll-marquee]').forEach(function (list) {
      if (interacted) {
        startMarquee(list);
        return;
      }
      // Wait for a real visitor before setting anything in motion.
      var kick = function () {
        startMarquee(list);
      };
      ['scroll', 'pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'].forEach(function (type) {
        window.addEventListener(type, kick, { once: true, passive: true });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Reveal on scroll
     ---------------------------------------------------------------------- */

  function initReveal(scope) {
    var nodes = Array.prototype.slice.call(scope.querySelectorAll('.ll-reveal'));
    if (!nodes.length) return;

    var settle = function () {
      nodes.forEach(function (node) {
        node.classList.add('is-in');
      });
    };

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      settle();
      return;
    }

    var io = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    nodes.forEach(function (node) {
      io.observe(node);
    });

    // Failsafe. IntersectionObserver callbacks are not delivered while the
    // document is hidden, which is exactly the state an OG-image renderer, a
    // prerenderer, a print job or a backgrounded tab is in. Without this, a
    // capture taken in that state gets the pre-reveal frame. The CSS keeps
    // opacity at 1 so nothing is ever invisible; this only settles the offset.
    var forceIn = function () {
      io.disconnect();
      settle();
    };
    if (document.visibilityState === 'hidden') {
      forceIn();
      return;
    }
    window.setTimeout(forceIn, 2000);
    document.addEventListener(
      'visibilitychange',
      function () {
        if (document.visibilityState === 'hidden') forceIn();
      },
      { once: true }
    );
    window.addEventListener('beforeprint', forceIn, { once: true });
  }

  /* ------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function boot(scope) {
    scope = scope || document;
    scope.querySelectorAll('.ll-lightbox').forEach(initLightbox);
    scope.querySelectorAll('[data-ll-sizeguide]').forEach(initSizeGuide);
    scope.querySelectorAll('[data-ll-stickybar]').forEach(initStickyBar);
    initPickerLabels(scope);
    initStats(scope);
    initMarquee(scope);
    initReveal(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // Dawn swaps product-info markup on variant/product change; re-bind after.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
  document.addEventListener('product-info:loaded', function (event) {
    boot(event.target);
  });
})();
